import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Button from '@amzn/meridian/button';
import Popover from '@amzn/meridian/popover';
import NumberField from './NumberField';
import Icon from '@amzn/meridian/icon';
import CopyTokens from '@amzn/meridian-tokens/base/icon/copy';
import shareTokens from '@amzn/meridian-tokens/base/icon/share';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import caretDownTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import { FloorplanViewer, FloorplanProvider, PolygonLayer, FloorplanViewerRef } from '@amzn/ihm-floorplan-viewer';
import { BayData, DisplayMode } from '@amzn/ihm-floorplan-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useDisplayModes from './displayModes';
import useTooltipFromDisplayModes from './useTooltipFromDisplayModes';
import DisplayModeSelector from './displayModeSelector';
import { FloorplanData } from './zoneMapDataType';
import { buildFixtureFieldMap } from './zoneMapDataHelper';
import SelectionToolbar from './SelectionToolbar';
import { ModifierKeys } from '../hooks/useFixtureSelection';
import { ZoneCreationProperties } from '../types/zoneMapType';

import { PickPathFeatureCollection } from '../pickPath/buildPickPathFeatureCollection';
import { FixtureAssignment } from '../allocation/computeFixtureAllocations';
import { SubcategoryAllocation } from '../types/zoneMapType';
import { downloadSvgAsFile } from '../utils/downloadSvg';
import { injectSvgGradients } from '../utils/injectSvgGradients';
import { BayMappingRow, buildBayMappingFileName, downloadBayMappingExcel } from '../utils/exportBayMapping';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { useFlowLabel } from '../utils/flowLabel';
import logger from '@utils/logger';

export interface ViewZoneComponentProps {
  floorplanData: FloorplanData[];
  fixtureIdToZoneDataMap: Record<
    string,
    {
      zoneName: string;
      zoneId: string;
      productTypes: string;
      isAgile: boolean;
      zoneNameKey: string;
      macroplanogramId?: string;
    }
  >;
  enableClick: boolean;

  // New props for selection support
  selectionEnabled?: boolean;
  activeZoneIdx?: number;
  zones?: Map<number, ZoneCreationProperties>;
  onFixtureSelect?: (fixtureId: string, modifierKeys: ModifierKeys) => void;
  onMarqueeAssign?: (fixtureIds: string[]) => void;
  onActiveZoneChange?: (zoneIdx: number) => void;

  // Pick path drawing props
  isDrawingPickPath?: boolean;
  pickPathLayers?: PickPathFeatureCollection;

  // Pick path toolbar controls
  onStartDrawing?: () => void;
  onStopDrawing?: () => void;
  onFinishPath?: () => void;
  onSubmitSequence?: () => void;
  hasPickPaths?: boolean;
  isSubmittingSequence?: boolean;

  // Fixture ordering data for Product Flow display mode
  fixtureOrderingMap?: Map<string, number>;

  // Fixture assignments for Subcategory Allocation display mode
  fixtureAssignments?: FixtureAssignment[];

  // Allocations for legend display
  allocations?: SubcategoryAllocation[];

  // GFC: per-Drop-Zone legend entries (color + name + fixture count)
  dropZoneLegendEntries?: { name: string; color: string; fixtureCount: number }[];

  // Whether a saved sequence already exists (blocks editing)
  hasExistingSequence?: boolean;

  // Department filter
  availableDepartments?: string[];
  selectedDepartment?: string;
  onDepartmentChange?: (dept: string) => void;

  // Drag-to-draw props
  onFixturePointerOver?: (fixtureId: string) => void;
  onPointerDownForPath?: (fixtureId: string) => void;

  // Undo props
  onUndoWaypoint?: () => void;
  canUndo?: boolean;

  // Display mode override (set by parent to force a specific mode, e.g. during drawing)
  displayModeOverride?: string;

  // Fixture count for progress tracking during drawing
  totalFixtureCount?: number;
  sequencedFixtureCount?: number;

  // Copy action buttons (shown in view mode)
  onCopyToNew?: () => void;
  onCopyToMultipleStores?: () => void;

  // GFC view-mode pick-path editing (toolbar). When onEditPickPath is provided,
  // an "Edit PickPath" button shows in view mode.
  onEditPickPath?: () => void;
  isEditingPickPath?: boolean;
  onSavePickPath?: () => void;
  onCancelPickPath?: () => void;
  onUndoPickPathFixture?: () => void;
  onClearPickPath?: () => void;
  isSavingPickPath?: boolean;
  editingPickPathCount?: number;
  // Custom numbering for the pick path: start number + step increment.
  pickPathStartNumber?: number;
  pickPathStepIncrement?: number;
  onPickPathStartChange?: (value: number) => void;
  onPickPathStepChange?: (value: number) => void;
  // ViewZoneComponent props for drag-to-draw during view-mode pick-path editing.
  onEditPointerDown?: (fixtureId: string) => void;
  onEditPointerOver?: (fixtureId: string) => void;
  onEditPointerUp?: () => void;

  // GFC create-mode pick path custom numbering (forwarded to SelectionToolbar).
  showCreateNumbering?: boolean;
  createStartNumber?: number;
  createStepIncrement?: number;
  onCreateStartNumberChange?: (value: number) => void;
  onCreateStepIncrementChange?: (value: number) => void;
}

const ViewZoneComponent = ({
  floorplanData,
  fixtureIdToZoneDataMap,
  enableClick,
  selectionEnabled = false,
  activeZoneIdx = 0,
  zones = new Map(),
  onFixtureSelect,
  onMarqueeAssign,
  onActiveZoneChange,
  isDrawingPickPath = false,
  pickPathLayers,
  onStartDrawing,
  onStopDrawing,
  onFinishPath,
  onSubmitSequence,
  hasPickPaths = false,
  isSubmittingSequence = false,
  hasExistingSequence = false,
  fixtureOrderingMap,
  fixtureAssignments,
  allocations,
  dropZoneLegendEntries,
  availableDepartments = [],
  selectedDepartment = '',
  onDepartmentChange,
  onCopyToNew,
  onCopyToMultipleStores,
  onEditPickPath,
  isEditingPickPath = false,
  onSavePickPath,
  onCancelPickPath,
  onUndoPickPathFixture,
  onClearPickPath,
  isSavingPickPath = false,
  editingPickPathCount = 0,
  pickPathStartNumber = 1,
  pickPathStepIncrement = 1,
  onPickPathStartChange,
  onPickPathStepChange,
  onEditPointerDown,
  onEditPointerOver,
  onEditPointerUp,
  showCreateNumbering = false,
  createStartNumber = 1,
  createStepIncrement = 1,
  onCreateStartNumberChange,
  onCreateStepIncrementChange,
  onFixturePointerOver,
  onPointerDownForPath,
  onUndoWaypoint,
  canUndo,
  displayModeOverride,
  totalFixtureCount,
  sequencedFixtureCount,
}: ViewZoneComponentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const floorplanRef = useRef<FloorplanViewerRef>(null);
  const config = useProgramConfig();
  const supportsDepartmentFilter = config.zoneMapBehavior.supportsDepartmentFilter;
  const flowLabel = useFlowLabel();

  // Shift-gated marquee for fixture assignment in "Create new zone map".
  // Holding Shift turns on the viewer's built-in box selection (and disables
  // camera pan), so Shift+left-drag draws a rectangle. The captured fixtures are
  // assigned to the active Drop Zone mirroring single-click assignment.
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  // Latch so `select` stays on if Shift is released mid-drag.
  const [marqueeDragging, setMarqueeDragging] = useState(false);
  const isMarqueeActive = selectionEnabled && !isDrawingPickPath && !isEditingPickPath && isShiftHeld;
  const isMarqueeSelecting = isMarqueeActive || marqueeDragging;

  const isShiftHeldRef = useRef(false);
  isShiftHeldRef.current = isShiftHeld;
  const isMarqueeActiveRef = useRef(false);
  isMarqueeActiveRef.current = isMarqueeActive;

  // Latest fixtures captured by the in-progress marquee. We assign once on
  // pointer-up (below) rather than on every selection change, so a growing/
  // shrinking rectangle doesn't spam assignments and toasts mid-drag.
  const marqueeSelectionRef = useRef<string[]>([]);

  useEffect(() => {
    if (!selectionEnabled) {
      setIsShiftHeld(false);
      setMarqueeDragging(false);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftHeld(true);
    };
    // Cancel: drop captured fixtures; latch keeps `select` on for viewer cleanup.
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftHeld(false);
        marqueeSelectionRef.current = [];
      }
    };
    // Window blur (e.g. tab switch) is also a cancel.
    const handleBlur = () => {
      setIsShiftHeld(false);
      marqueeSelectionRef.current = [];
    };
    const handlePointerUp = () => {
      if (isShiftHeldRef.current && marqueeSelectionRef.current.length > 0) {
        onMarqueeAssign?.(marqueeSelectionRef.current);
      }
      marqueeSelectionRef.current = [];
      floorplanRef.current?.setSelectedFixtureIds([]);
      setMarqueeDragging(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [selectionEnabled, onMarqueeAssign]);

  // Latch drag start so `select` survives Shift release.
  const handleContainerPointerDown = useCallback(() => {
    if (isMarqueeActiveRef.current) setMarqueeDragging(true);
  }, []);

  // Shared metadata read from the URL for both the SVG and Excel exports.
  // Keeping this in one place avoids drift in the fallback strings and lets
  // the export callbacks depend on the values rather than on `searchParams`.
  const storeId = searchParams.get('storeId') ?? 'unknown';
  const dateStr = searchParams.get('date') ?? 'unknown';

  // Zone map snapshot download — uses FloorplanViewer's built-in SVG export
  const handleDownloadSnapshot = useCallback(() => {
    if (!floorplanRef.current?.exportToSvg) {
      // eslint-disable-next-line no-console
      console.warn('SVG export not available in this version of FloorplanViewer');
      return;
    }
    let svg = floorplanRef.current.exportToSvg({
      title: `Zone Map — ${storeId}`,
    });
    if (!svg) return;

    // Post-process: inject boundary gradients and legend table
    svg = injectSvgGradients(svg, fixtureAssignments ?? [], allocations);

    const deptSuffix = selectedDepartment ? `-${selectedDepartment}` : '';
    downloadSvgAsFile(svg, `zonemap-${storeId}-${dateStr}${deptSuffix}.svg`);
  }, [storeId, dateStr, selectedDepartment, fixtureAssignments, allocations]);

  // Bay → Zone → Pick-Path ID Excel download.
  // Bays without both a zone and a pick path id are omitted.
  const handleDownloadBayMapping = useCallback(() => {
    const assignmentByFixture = new Map<string, FixtureAssignment>();
    for (const assignment of fixtureAssignments ?? []) {
      assignmentByFixture.set(assignment.fixtureId, assignment);
    }

    const resolveZoneName = (fixtureId: string): string => {
      const assignment = assignmentByFixture.get(fixtureId);
      if (assignment?.subcategoryName) return assignment.subcategoryName;
      return fixtureIdToZoneDataMap[fixtureId]?.zoneName ?? '';
    };

    const rows: BayMappingRow[] = floorplanData
      .map((fixture) => ({
        fixtureId: fixture.id,
        zoneName: resolveZoneName(fixture.id),
        pickPathId: fixtureOrderingMap?.get(fixture.id),
      }))
      .filter((row): row is BayMappingRow => row.zoneName.length > 0 && row.pickPathId !== undefined);

    const fileName = buildBayMappingFileName(storeId, dateStr, selectedDepartment || undefined);

    const zoneLabel = config.macroplanogramScope === 'category' ? config.labels.category : config.labels.subcategory;

    void downloadBayMappingExcel(rows, fileName, zoneLabel, flowLabel.title).catch((err) => {
      void logger.error('Bay mapping export failed', err instanceof Error ? err : new Error(String(err)));
    });
  }, [
    floorplanData,
    fixtureIdToZoneDataMap,
    fixtureAssignments,
    fixtureOrderingMap,
    storeId,
    dateStr,
    selectedDepartment,
    config,
    flowLabel,
  ]);

  // Hover-driven export dropdown. We open on mouseEnter and close on mouseLeave
  // with a short grace period so the user can travel from the trigger button
  // into the popover content without it disappearing under their cursor.
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportCloseTimerRef = useRef<number | null>(null);

  const cancelExportClose = useCallback(() => {
    if (exportCloseTimerRef.current !== null) {
      window.clearTimeout(exportCloseTimerRef.current);
      exportCloseTimerRef.current = null;
    }
  }, []);

  const scheduleExportClose = useCallback(() => {
    cancelExportClose();
    exportCloseTimerRef.current = window.setTimeout(() => {
      setExportMenuOpen(false);
      exportCloseTimerRef.current = null;
    }, 200);
  }, [cancelExportClose]);

  const openExportMenu = useCallback(() => {
    cancelExportClose();
    setExportMenuOpen(true);
  }, [cancelExportClose]);

  // Cleanup on unmount.
  useEffect(() => () => cancelExportClose(), [cancelExportClose]);

  const runExport = useCallback(
    (action: () => void) => () => {
      action();
      cancelExportClose();
      setExportMenuOpen(false);
    },
    [cancelExportClose],
  );

  // Build fixture → department map from floorplan data
  const fixtureDepartmentMap = useMemo(() => buildFixtureFieldMap(floorplanData, (f) => f.department), [floorplanData]);
  // Build fixture → fixtureType map from floorplan data
  const fixtureTypeMap = useMemo(() => buildFixtureFieldMap(floorplanData, (f) => f.fixtureType), [floorplanData]);
  const displayModes = useDisplayModes(
    fixtureIdToZoneDataMap,
    fixtureAssignments,
    allocations,
    fixtureDepartmentMap,
    fixtureOrderingMap,
    fixtureTypeMap,
    dropZoneLegendEntries,
  );
  const [displayMode, setDisplayMode] = useState<DisplayMode | null>(displayModes.defaultOption);
  const [hasAutoSwitched, setHasAutoSwitched] = useState(false);
  const tooltip = useTooltipFromDisplayModes(displayModes.optionsList);

  // Auto-switch display mode based on available data (only on initial load)
  useEffect(() => {
    if (hasAutoSwitched) return;

    // Prefer Subcategory Allocation when fixture assignments exist (works for both all-fixtures and department modes)
    if (fixtureAssignments && fixtureAssignments.length > 0) {
      const allocMode = displayModes.optionsList.find((m) => m.id === 'SUBCATEGORY_ALLOCATION');
      if (allocMode) {
        setDisplayMode(allocMode);
        setHasAutoSwitched(true);
        return;
      }
    }

    // Fall back to Product Flow if ordering data exists
    if (fixtureOrderingMap && fixtureOrderingMap.size > 0) {
      const pickSeqMode = displayModes.optionsList.find((m) => m.id === 'PICK_SEQUENCE');
      if (pickSeqMode) {
        setDisplayMode(pickSeqMode);
        setHasAutoSwitched(true);
      }
    }
  }, [fixtureOrderingMap, fixtureAssignments, displayModes.optionsList, hasAutoSwitched]);

  // Reset auto-switch when department selection changes so the correct default mode is picked
  useEffect(() => {
    setHasAutoSwitched(false);
  }, [selectedDepartment]);

  // Apply display mode override from parent (e.g. during drawing mode)
  useEffect(() => {
    if (displayModeOverride) {
      const mode = displayModes.optionsList.find((m) => m.id === displayModeOverride);
      if (mode) {
        setDisplayMode(mode);
      }
    }
  }, [displayModeOverride, displayModes.optionsList]);

  // Auto-switch to Product Flow mode while editing the pick path so sequence
  // numbers are visible; the overlay renders the editing order. On exit,
  // revert to the mode that was active before editing began (mirrors the
  // drawing-mode pattern below). Without this revert the viewer would stay
  // stuck in PICK_SEQUENCE after the user cancels or saves.
  const prevIsEditingRef = React.useRef(isEditingPickPath);
  const preEditDisplayModeRef = React.useRef<DisplayMode | null>(null);
  useEffect(() => {
    const wasEditing = prevIsEditingRef.current;
    prevIsEditingRef.current = isEditingPickPath;

    if (isEditingPickPath && !wasEditing) {
      // Entering edit mode — remember what the user was looking at, then
      // switch to Product Flow.
      preEditDisplayModeRef.current = displayMode;
      const pickSeqMode = displayModes.optionsList.find((m) => m.id === 'PICK_SEQUENCE');
      if (pickSeqMode) setDisplayMode(pickSeqMode);
    } else if (!isEditingPickPath && wasEditing) {
      // Leaving edit mode — restore the prior mode if we still have it,
      // otherwise fall back to Subcategory Allocation, then the default.
      const prior = preEditDisplayModeRef.current;
      const stillAvailable = prior && displayModes.optionsList.some((m) => m.id === prior.id);
      const fallback =
        displayModes.optionsList.find((m) => m.id === 'SUBCATEGORY_ALLOCATION') ?? displayModes.defaultOption;
      setDisplayMode(stillAvailable ? prior : fallback);
      preEditDisplayModeRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingPickPath, displayModes.optionsList]);

  // End a drag stroke on pointer release anywhere while editing the pick path.
  useEffect(() => {
    if (!isEditingPickPath || !onEditPointerUp) return;
    window.addEventListener('pointerup', onEditPointerUp);
    return () => window.removeEventListener('pointerup', onEditPointerUp);
  }, [isEditingPickPath, onEditPointerUp]);

  // Track previous drawing state to detect transitions
  const prevIsDrawingRef = React.useRef(isDrawingPickPath);

  // Auto-switch to Product Flow mode when drawing starts, revert when drawing stops
  useEffect(() => {
    const wasDrawing = prevIsDrawingRef.current;
    prevIsDrawingRef.current = isDrawingPickPath;

    if (isDrawingPickPath && !wasDrawing) {
      // Entering drawing mode → switch to Product Flow
      const pickSeqMode = displayModes.optionsList.find((m) => m.id === 'PICK_SEQUENCE');
      if (pickSeqMode) {
        setDisplayMode(pickSeqMode);
      }
    } else if (!isDrawingPickPath && wasDrawing) {
      // Leaving drawing mode → switch back to Subcategory Allocation
      const allocMode = displayModes.optionsList.find((m) => m.id === 'SUBCATEGORY_ALLOCATION');
      if (allocMode) {
        setDisplayMode(allocMode);
      }
    }
  }, [isDrawingPickPath, displayModes.optionsList]);

  // Render pick path overlay: in create/edit mode, while editing in view mode,
  // or in Product Flow display mode for plain view.
  const isEditMode = onStartDrawing !== undefined;
  const pickPathOverlay =
    pickPathLayers &&
    pickPathLayers.features.length > 0 &&
    (isEditMode || isEditingPickPath || displayMode?.id === 'PICK_SEQUENCE') ? (
      <PolygonLayer data={pickPathLayers} defaultStyle={{ showArrows: true, outlineStyle: 'solid' }} />
    ) : null;
  const getZoneIdByFixtureId = (
    mapping: Record<string, { zoneName: string; zoneId: string; isAgile: boolean; macroplanogramId?: string }>,
    fixtureId?: string,
  ): { zoneId: string; isAgile: boolean; macroplanogramId: string } => {
    if (fixtureId && fixtureId in mapping) {
      return {
        zoneId: mapping[fixtureId].zoneId,
        isAgile: mapping[fixtureId].isAgile,
        macroplanogramId: mapping[fixtureId].macroplanogramId ?? '',
      };
    }
    return {
      zoneId: '',
      isAgile: false,
      macroplanogramId: '',
    };
  };
  const handleFixtureClick = (fixtureData: BayData, event?: MouseEvent) => {
    // View-mode pick-path editing: fixtures are added via pointer drag (see
    // onFixturePointerDown / onFixtureHover below). Swallow plain clicks so they
    // don't fall through to selection/navigation. Removal is via Undo/Clear.
    if (isEditingPickPath) {
      return;
    }

    // If selection is enabled, handle fixture selection
    if (selectionEnabled && onFixtureSelect && fixtureData.id) {
      const modifierKeys: ModifierKeys = {
        shift: event?.shiftKey || false,
        ctrl: event?.ctrlKey || false,
        meta: event?.metaKey || false,
      };
      onFixtureSelect(fixtureData.id, modifierKeys);
      return;
    }

    // In all-fixtures mode (no department selected), clicking a fixture switches
    // to that fixture's department view instead of navigating to agile management.
    if (!selectedDepartment && onDepartmentChange && fixtureData.id) {
      const dept = fixtureDepartmentMap.get(fixtureData.id);
      if (dept) {
        onDepartmentChange(dept);
        return;
      }
    }

    // Otherwise, handle navigation to Item Assortment Management page
    // Note on zoneId semantics:
    //   - In the zone map, zoneId = subcategory ID (from MacroData.zoneId)
    //   - In the URL, zoneId = macroplanogram ID (used by ItemAssortmentManagement as macroplanogramId)
    //   - subcategoryId in the URL = the zone map's zoneId (the actual subcategory)
    const { zoneId, isAgile, macroplanogramId } = getZoneIdByFixtureId(fixtureIdToZoneDataMap, fixtureData.id);
    if (isAgile && macroplanogramId) {
      const storeId = searchParams.get('storeId') || '';
      const date = searchParams.get('date') || '';
      const params = new URLSearchParams({
        storeId,
        date,
        zoneId: macroplanogramId,
        subcategoryId: zoneId,
      });
      // Navigate relative to the current route's parent (e.g., /wholefoods/ or /fresh/)
      navigate(`../agileManagement?${params}`);
    }
  };

  const handleFloorplanSelectionChange = useCallback((selectedFixtureIds: string[]) => {
    if (isMarqueeActiveRef.current && selectedFixtureIds.length > 1) {
      marqueeSelectionRef.current = selectedFixtureIds;
    }
  }, []);

  useEffect(() => {
    // when fixtureIdToZoneDataMap changes, reset zone displayMode
    const currentDisplayMode = displayModes.optionsList.find((mode) => mode.id === displayMode?.id) as DisplayMode;
    setDisplayMode(currentDisplayMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtureIdToZoneDataMap]);

  return displayMode ? (
    <Column height="100%">
      {/* Selection Toolbar - only show when selection is enabled */}
      {selectionEnabled && onActiveZoneChange && (
        <SelectionToolbar
          activeZoneIdx={activeZoneIdx}
          zones={zones}
          onActiveZoneChange={onActiveZoneChange}
          disabled={false}
          isDrawingPickPath={isDrawingPickPath}
          onStartDrawing={onStartDrawing}
          onStopDrawing={onStopDrawing}
          onFinishPath={onFinishPath}
          onSubmitSequence={onSubmitSequence}
          hasPickPaths={hasPickPaths}
          isSubmittingSequence={isSubmittingSequence}
          hasExistingSequence={hasExistingSequence}
          onUndoWaypoint={onUndoWaypoint}
          canUndo={canUndo}
          totalFixtureCount={totalFixtureCount}
          sequencedFixtureCount={sequencedFixtureCount}
          showNumbering={showCreateNumbering}
          startNumber={createStartNumber}
          stepIncrement={createStepIncrement}
          onStartNumberChange={onCreateStartNumberChange}
          onStepIncrementChange={onCreateStepIncrementChange}
          flowLabel={flowLabel}
        />
      )}

      <Row spacingInset="none" alignmentHorizontal="center" alignmentVertical="bottom" spacing="400">
        {supportsDepartmentFilter && availableDepartments.length > 0 && onDepartmentChange && (
          <Select
            label="Department"
            value={selectedDepartment}
            onChange={onDepartmentChange}
            placeholder="All fixtures"
            width={200}
            size="small"
          >
            <SelectOption label="All fixtures" value="" />
            {availableDepartments.map((dept) => (
              <SelectOption label={dept} value={dept} key={dept} />
            ))}
          </Select>
        )}
        <DisplayModeSelector
          displayModes={displayModes.optionsList}
          selectedDisplayMode={displayMode}
          setSelectedDisplayMode={setDisplayMode}
        />
        {enableClick && onCopyToNew && !isEditingPickPath && (
          <Button size="small" type="tertiary" onClick={onCopyToNew}>
            <Icon tokens={CopyTokens} />
            Copy to new
          </Button>
        )}
        {enableClick && onCopyToMultipleStores && !isEditingPickPath && (
          <Button size="small" type="tertiary" onClick={onCopyToMultipleStores}>
            <Icon tokens={shareTokens} />
            Copy to multiple stores
          </Button>
        )}
        {enableClick && onEditPickPath && !isEditingPickPath && (
          <Button size="small" type="tertiary" onClick={onEditPickPath}>
            Edit {flowLabel.title}
          </Button>
        )}
        {isEditingPickPath && (
          <>
            <NumberField
              label="Start #"
              value={pickPathStartNumber}
              min={0}
              onCommit={(n) => onPickPathStartChange?.(n)}
            />
            <NumberField
              label="Step"
              value={pickPathStepIncrement}
              min={1}
              onCommit={(n) => onPickPathStepChange?.(n)}
            />
            <Button size="small" type="primary" onClick={onSavePickPath} disabled={isSavingPickPath}>
              {isSavingPickPath ? 'Saving…' : `Save ${flowLabel.title} (${editingPickPathCount})`}
            </Button>
            <Button size="small" type="tertiary" onClick={onUndoPickPathFixture} disabled={editingPickPathCount === 0}>
              Undo
            </Button>
            <Button size="small" type="tertiary" onClick={onClearPickPath} disabled={editingPickPathCount === 0}>
              Clear
            </Button>
            <Button size="small" type="link" onClick={onCancelPickPath} disabled={isSavingPickPath}>
              Cancel
            </Button>
          </>
        )}
        {(fixtureAssignments?.length ?? 0) > 0 && !isEditingPickPath && (
          <div onMouseEnter={openExportMenu} onMouseLeave={scheduleExportClose} style={{ display: 'inline-block' }}>
            <Button
              size="small"
              type="tertiary"
              ref={exportButtonRef}
              onClick={openExportMenu}
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
            >
              <Icon tokens={downloadLargeTokens} />
              Export
              <Icon tokens={caretDownTokens} />
            </Button>
            <Popover
              anchorNode={exportButtonRef.current || undefined}
              open={exportMenuOpen}
              onClose={() => setExportMenuOpen(false)}
              position="bottom"
              alignment="end"
            >
              <div onMouseEnter={cancelExportClose} onMouseLeave={scheduleExportClose} role="menu">
                <Column spacing="100" alignmentHorizontal="start">
                  <Button size="small" type="link" onClick={runExport(handleDownloadSnapshot)}>
                    Export Floorplan as SVG
                  </Button>
                  {config.macroplanogramScope === 'category' && (
                    <Button size="small" type="link" onClick={runExport(handleDownloadBayMapping)}>
                      Export Bay Mapping as Excel
                    </Button>
                  )}
                </Column>
              </div>
            </Popover>
          </div>
        )}
      </Row>
      <Row height="100%" widths={['fill', 'fit']} alignmentVertical="top">
        <div
          style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center' }}
          onPointerDown={handleContainerPointerDown}
        >
          <FloorplanProvider>
            {(enableClick || selectionEnabled || isDrawingPickPath) && (
              <FloorplanViewer
                ref={floorplanRef}
                fixtureData={floorplanData}
                displayMode={displayMode}
                fixtureTooltip={tooltip}
                select={isMarqueeSelecting}
                onSelectedFixturesChange={handleFloorplanSelectionChange}
                onFixtureClick={handleFixtureClick}
                onFixtureHover={
                  isDrawingPickPath && onFixturePointerOver
                    ? (fixture: BayData | null) => {
                        if (fixture?.id) onFixturePointerOver(fixture.id);
                      }
                    : isEditingPickPath && onEditPointerOver
                      ? (fixture: BayData | null) => {
                          if (fixture?.id) onEditPointerOver(fixture.id);
                        }
                      : undefined
                }
                onFixturePointerDown={
                  isDrawingPickPath && onPointerDownForPath
                    ? (fixture: BayData) => {
                        if (fixture.id) onPointerDownForPath(fixture.id);
                      }
                    : isEditingPickPath && onEditPointerDown
                      ? (fixture: BayData) => {
                          if (fixture.id) onEditPointerDown(fixture.id);
                        }
                      : undefined
                }
                enablePan={!isDrawingPickPath && !isEditingPickPath && !isMarqueeSelecting}
              >
                {pickPathOverlay}
              </FloorplanViewer>
            )}
            {!enableClick && !selectionEnabled && !isDrawingPickPath && (
              <FloorplanViewer
                ref={floorplanRef}
                fixtureData={floorplanData}
                displayMode={displayMode}
                fixtureTooltip={tooltip}
              >
                {pickPathOverlay}
              </FloorplanViewer>
            )}
          </FloorplanProvider>
        </div>
        <Column alignmentHorizontal="start" height="100%">
          {displayMode.legend !== undefined && displayMode.legend}
        </Column>
      </Row>
    </Column>
  ) : null;
};

export default ViewZoneComponent;
